import { useState } from "react";
import { Fragment } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import QuestionAnswerRoundedIcon from "@mui/icons-material/QuestionAnswerRounded";
import { Box, Grid } from "@mui/material";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tab from "@mui/material/Tab";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { v4 as uuid } from "uuid";

import { createQuestionData } from "../../assets/dataSchema";
import { deepCopyObject } from "../../utils/helpers";
import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogTab from "../ui/omniDialogTab";
import DialogTabs from "../ui/omniDialogTabs";
import DialogTitle from "../ui/omniDialogTitle";
import OmniEditButton from "../ui/omniEditButton";
import OmniResponsiveButton from "../ui/omniResponsiveButton";
import OmniTextField from "../ui/omniTextField";
import WidgetList from "../ui/widgetList";
import AlertDialog from "./alertDialog";
import PracticeAssessmentTemplateQuestionDialog from "./practiceAssessmentTemplateQuestionDialog";

const PracticeAssessmentTemplateDialog = props => {
  const {
    open,
    dialogTitle = "Add Assessment",
    okMessage = "OK",
    cancelMessage = "Cancel",
    deleteMessage = "remove",
    maxWidth = "sm",
    fullWidth = true,
    initialValues,
  } = props;

  const [editedValues] = useState(initialValues);
  const [title, setTitle] = useState(initialValues.omniData.title);
  const [about, setAbout] = useState(initialValues.omniData.about);
  const [duration, setDuration] = useState(initialValues.omniData.duration);
  const [directions, setDirections] = useState(initialValues.omniData.directions);
  const [attribution, setAttribution] = useState(initialValues.omniData.attribution);
  const [questions, setQuestions] = useState(initialValues.omniData.questions);

  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  const moveQuestion = (from, to) => {
    let reorderedQuestions = deepCopyObject(questions);
    reorderedQuestions.splice(to, 0, reorderedQuestions.splice(from, 1)[0]);

    return reorderedQuestions;
  };

  const removeQuestion = id => {
    let updatedQuestions = deepCopyObject(questions);
    updatedQuestions.splice(
      updatedQuestions.findIndex(question => question.omniID === id),
      1,
    );
    return updatedQuestions;
  };

  const updateAssessmentTemplateQuestions = updatedQuestion => {
    let questionIndex = questions.findIndex(question => question.omniID === updatedQuestion.omniID);

    if (questionIndex >= 0) {
      //We're modifying here
      setQuestions(prevState =>
        prevState.map(question =>
          question.omniID === updatedQuestion.omniID ? (question = updatedQuestion) : question,
        ),
      );
    } else {
      updatedQuestion.omniID = uuid();
      setQuestions(prevState => [...prevState, updatedQuestion]);
    }
  };

  const onDeleteHandler = () => {
    props.onDelete(initialValues);
  };

  const onAcceptHandler = () => {
    //notice we didn't touch the id
    // unless we do for the time being
    if (!editedValues._id) {
      editedValues._id = uuid();
    }

    editedValues.omniData.title = title;
    editedValues.omniData.about = about;
    editedValues.omniData.duration = duration;
    editedValues.omniData.directions = directions;
    editedValues.omniData.attribution = attribution;
    editedValues.omniData.questions = questions;
    props.onAccept(editedValues);
  };

  const onCloseHandler = () => {
    props.onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onCloseHandler}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>
      <DialogTabs
        tabIndex={tabIndex}
        setTabIndex={setTabIndex}
        variant="fullWidth"
        scrollButtons
        allowScrollButtonsMobile
        indicatorColor="primary"
        textColor="inherit"
      >
        <Tab key={`tab0`} icon={<InfoRoundedIcon />} label="Information" />
        <Tab key={`tab2`} icon={<QuestionAnswerRoundedIcon />} label="Questions" />
      </DialogTabs>
      <DialogContent>
        <DialogTab selectedIndex={tabIndex} index={0}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              webkitUserSelect: "none",
              userSelect: "none",
              height: 400,
              paddingRight: "0px",
            }}
          >
            <OmniTextField
              required
              key={`title`}
              label="Title"
              type="textInput"
              name={uuid()} //prevents AutoComplete Suggestions
              variant="outlined"
              value={title || ""}
              onChange={event => {
                setTitle(event.target.value);
              }}
              placeholder={"The Title of the assessment"}
            />

            <OmniTextField
              key={`about`}
              label="About"
              type="textInput"
              name={uuid()} //prevents AutoComplete Suggestions
              variant="outlined"
              multiline
              rows={4}
              value={about || ""}
              onChange={event => {
                setAbout(event.target.value);
              }}
              placeholder={"Short description about the assessment"}
            />

            <OmniTextField
              key={`estimatedDuration`}
              label="Estimated Duration"
              type="textInput"
              name={uuid()} //prevents AutoComplete Suggestions
              variant="outlined"
              value={duration || ""}
              onChange={event => {
                setDuration(event.target.value);
              }}
              placeholder={
                "Estimate the length of time that is should take to complete the assessment"
              }
            />

            <OmniTextField
              key={`directions`}
              label="Directions"
              type="textInput"
              name={uuid()} //prevents AutoComplete Suggestions
              variant="outlined"
              multiline
              rows={4}
              value={directions || ""}
              onChange={event => {
                setDirections(event.target.value);
              }}
              placeholder={"Directions for completing the assessment"}
            />

            <OmniTextField
              key={`attribution`}
              label="Attribution"
              type="textInput"
              name={uuid()} //prevents AutoComplete Suggestions
              variant="outlined"
              value={attribution || ""}
              onChange={event => {
                setAttribution(event.target.value);
              }}
              placeholder={"Original creator of this assessment"}
            />
          </Box>
        </DialogTab>

        <DialogTab selectedIndex={tabIndex} index={1}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              webkitUserSelect: "none",
              userSelect: "none",
              height: 400,
              paddingRight: "0px",
            }}
          >
            <Button
              color="secondary"
              variant="contained"
              onClick={() => setSelectedQuestion(createQuestionData)}
            >
              Add Question
            </Button>

            <WidgetList>
              {questions.map((question, index) => {
                return (
                  <ListItem key={`question${index}`} disableGutters={true} dense={true}>
                    <ListItemIcon>
                      <Tooltip title={"Move Question Up"} placement="right">
                        <Fragment>
                          <IconButton
                            disabled={index === 0}
                            onClick={e => {
                              e.stopPropagation();
                              setQuestions(moveQuestion(index, index - 1));
                            }}
                            size="large"
                          >
                            <KeyboardArrowUpRoundedIcon />
                          </IconButton>
                        </Fragment>
                      </Tooltip>
                    </ListItemIcon>
                    <ListItemIcon>
                      <Tooltip title={"Move Question Down"} placement="right">
                        <Fragment>
                          <IconButton
                            disabled={index === questions.length - 1 || index === 0}
                            onClick={e => {
                              e.stopPropagation();
                              setQuestions(moveQuestion(index, index + 1));
                            }}
                            size="large"
                          >
                            <KeyboardArrowDownRoundedIcon />
                          </IconButton>
                        </Fragment>
                      </Tooltip>
                    </ListItemIcon>
                    <ListItemIcon>
                      <OmniEditButton
                        title="Delete Question"
                        placement="right"
                        size="large"
                        onClick={e => {
                          e.stopPropagation();
                          setQuestions(removeQuestion(question.omniID));
                        }}
                        icon={<DeleteRoundedIcon />}
                      />
                      {/* <Tooltip title={"Delete Question"} placement="right">
                          <IconButton
                            onClick={e => {
                              e.stopPropagation();
                              setQuestions(removeQuestion(question.omniID));
                            }}
                            size="large"
                          >
                            <DeleteRoundedIcon />
                          </IconButton>
                        </Tooltip> */}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body1">
                          {`${question.numberText ? `${question.numberText}: ` : ``}${
                            question.text
                          }`}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2">
                          {`${question.answerOptions.length} Answer Option${
                            question.answerOptions.length !== 1 ? `s` : ""
                          }`}
                        </Typography>
                      }
                    />
                    <OmniEditButton
                      onClick={e => {
                        e.stopPropagation();
                        setSelectedQuestion(question);
                      }}
                    />
                  </ListItem>
                );
              })}
            </WidgetList>
          </Box>
        </DialogTab>
      </DialogContent>

      <DialogActions>
        <Grid container justifyContent="space-between">
          <Grid item xs={6}>
            {initialValues._id != null && (
              <OmniResponsiveButton
                icon={<DeleteIcon />}
                onClick={() => setShowConfirm(true)}
                buttonText={deleteMessage}
                tooltipTitle=""
              />
            )}
          </Grid>
          <Grid container item justifyContent="flex-end" xs={6} spacing={1}>
            <Grid item>
              {cancelMessage && (
                <Button onClick={onCloseHandler} color="secondary" variant="contained">
                  {cancelMessage}
                </Button>
              )}
            </Grid>
            <Grid item>
              <Button onClick={onAcceptHandler} color="secondary" variant="contained">
                {okMessage}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </DialogActions>

      {showConfirm && (
        <AlertDialog
          open={showConfirm}
          dialogTitle={`Confirm`}
          dialogMessage={"Are you sure you want to remove this assessment?"}
          onAccept={() => {
            onDeleteHandler();
            setShowConfirm(false);
          }}
          onClose={() => {
            setShowConfirm(false);
          }}
        />
      )}
      {selectedQuestion && (
        <PracticeAssessmentTemplateQuestionDialog
          open={selectedQuestion != null}
          dialogTitle={`${selectedQuestion.omniID == null ? "Add" : "Edit"} Question`}
          initialValues={deepCopyObject(selectedQuestion)}
          onDelete={() => {
            setSelectedQuestion(null);
          }}
          onAccept={selectedQuestion => {
            updateAssessmentTemplateQuestions(selectedQuestion);
            setSelectedQuestion(null);
          }}
          onClose={() => {
            setSelectedQuestion(null);
          }}
        />
      )}
    </Dialog>
  );
};

export default PracticeAssessmentTemplateDialog;
